/* eslint-disable max-lines */
/* TODO: remove the above line and reduce the line count */

// Libs.
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { compose, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

// Assets.
import packageJson from '../../package.json';
import { appInit, pushSnackbar } from 'FRONTEND/actions/app';
import createAddressSearchField from 'FRONTEND/components/AddressSearchFieldComponent';
import createAreaField from 'FRONTEND/components/AreaField';
import createBillingPages from 'FRONTEND/components/BillingPages';
import createBodySelectionField from 'FRONTEND/components/BodySelectionField';
import createBreadcrumb from 'FRONTEND/components/Breadcrumbs';
import createCalculatedField from 'FRONTEND/components/CalculatedField';
import createNewAppointments from 'FRONTEND/components/Calendar/AppointmentForm';
import createNextAppointmentsList from 'FRONTEND/components/Calendar/AppointmentList';
import createCalendar from 'FRONTEND/components/Calendar/Calendar';
import createUserCalendarSettings from 'FRONTEND/components/Calendar/UserCalendarSettings';
import createSidePanelCalendar from 'FRONTEND/components/CalendarSidePanel';
import createConsultationNav from 'FRONTEND/components/ConsultationNav';
import createConsultationTable from 'FRONTEND/components/ConsultationTable';
import createCopyButton from 'FRONTEND/components/CopyButton';
import createCustomTooltip from 'FRONTEND/components/CustomTooltip';
import createDateTimeField from 'FRONTEND/components/DateTimeField';
import createDicomViewer from 'FRONTEND/components/DicomViewer';
import createDrugReferencesTable from 'FRONTEND/components/DrugReferencesTable';
import createEPrescription from 'FRONTEND/components/EPrescription';
import createExtendButton from 'FRONTEND/components/ExtendButton';
import createExternalZoll from 'FRONTEND/components/Externals/Zoll';
import createFileUploadField from 'FRONTEND/components/FileUploadField';
import createGenericActionBar from 'FRONTEND/components/GenericActionBar';
import createGeolocation from 'FRONTEND/components/Geolocation';
import createListActionBar from 'FRONTEND/components/ListActionBar';
import createMedicalFollowUp from 'FRONTEND/components/MedicalFollowUp';
import createMedicalMeasures from 'FRONTEND/components/MedicalMeasures';
import createNavBar from 'FRONTEND/components/NavBar';
import createSidePanelPatient from 'FRONTEND/components/PatientSidePanel';
import createProfileDropdown from 'FRONTEND/components/ProfileDropdown';
import createRecordings from 'FRONTEND/components/Recordings';
import createRepeatableField from 'FRONTEND/components/RepeatableField';
import createRightPanel from 'FRONTEND/components/RightPanel';
import createSignUp from 'FRONTEND/components/SignUp';
import createSnackbarDispatcher from 'FRONTEND/components/SnackbarDispatcher';
import createStethoscope from 'FRONTEND/components/Stethoscope';
import createUserBadge from 'FRONTEND/components/UserBadge';
import { createMiddlewares } from 'FRONTEND/middlewares';
import reducer from 'FRONTEND/reducers';
import { checkBrowser } from 'FRONTEND/utils/browser';
import { PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID, PJAX_EVENTS_CONTAINER_ID } from 'LEGACY/constants';

// Actions.

// FIXME: this won't work as process is a Node.js global.
// The intended behavior was probably to use the Webpack DefinePlugin or EnvironmentPlugin.
const isProduction = process.env.NODE_ENV === 'production';

if (isProduction) {
  Sentry.init({
    dsn: 'https://9460d3aedb7e40828ded71baa8995446@o80863.ingest.sentry.io/5443445',
    environment: window.environment,
    integrations: [Sentry.captureConsoleIntegration(), Sentry.extraErrorDataIntegration()],
    release: `cloud_frontend@${packageJson.version}`,
  });
  Sentry.setUser({
    id: window.userId,
    ip_address: '{{auto}}',
  });
}

/**
 * List of objects describing react trees. Each include:
 * a `targetId` or `targetClass` used to find the DOM node to be the tree's root
 * a `pjaxContainerDependencyIdList` with the ID of the pjax containers that are supposed to trigger the loading of the tree
 * a `create` function responsible for the actual instantiation of the React tree (calls ReactDOM.render on the tree root). May return an unmounting function responsible for cleanly removing the React tree from the DOM.
 *
 * @type Array<{
 *    targetId/targetClass: {String},
 *    create: {Function},
 *    pjaxContainerDependencyIdList: Array<String>,
 *  }>
 */
const reactTreeList = [
  {
    targetId: 'react_navBar',
    create: createNavBar,
    pjaxContainerDependencyIdList: [],
  },
  {
    targetId: 'react_extendButton',
    create: createExtendButton,
    pjaxContainerDependencyIdList: [],
  },
  {
    targetId: 'react_menuSettings',
    create: createProfileDropdown,
    pjaxContainerDependencyIdList: [],
  },
  {
    targetId: 'react_snackbarDispatcher',
    create: createSnackbarDispatcher,
    pjaxContainerDependencyIdList: [],
  },
  {
    targetId: 'react_sidePanelCalendar',
    create: createSidePanelCalendar,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
    shouldUnmountOnBeforeReplace: true,
  },
  {
    targetId: 'react_sidePanelPatient',
    create: createSidePanelPatient,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
    shouldUnmountOnBeforeReplace: true,
  },
  {
    targetId: 'react_genericActionBar',
    create: createGenericActionBar,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_listActionBar',
    create: createListActionBar,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_breadcrumbs',
    create: createBreadcrumb,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_consultationNav',
    create: createConsultationNav,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_calendar',
    create: createCalendar,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_newAppointments',
    create: createNewAppointments,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_userCalendarSettings',
    create: createUserCalendarSettings,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_medicalFollowUp',
    create: createMedicalFollowUp,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_drugReferencesTable',
    create: createDrugReferencesTable,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_nextAppointmentsList',
    create: createNextAppointmentsList,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_consultationTable',
    create: createConsultationTable,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_stethoscope',
    create: createStethoscope,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetClass: 'react_billingPages',
    create: createBillingPages,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetClass: 'react_copyButton',
    create: createCopyButton,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetClass: 'react_dicomViewer',
    create: createDicomViewer,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetClass: 'react_areaField',
    create: createAreaField,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_bodySelectionField',
    create: createBodySelectionField,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_addressSearchField',
    create: createAddressSearchField,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_calculatedField',
    create: createCalculatedField,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_dateTimeField',
    create: createDateTimeField,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_ePrescription',
    create: createEPrescription,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_fileUploadField',
    create: createFileUploadField,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_medicalMeasures',
    create: createMedicalMeasures,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_geolocation',
    create: createGeolocation,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetClass: 'react_repeatableField',
    create: createRepeatableField,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_TRANSITION_CONTAINER_ID],
  },
  {
    targetClass: 'react_externalsZoll',
    create: createExternalZoll,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetId: 'react_rightPanel',
    create: createRightPanel,
    pjaxContainerDependencyIdList: [],
  },
  {
    targetClass: 'react_recordings',
    create: createRecordings,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID],
  },
  {
    targetClass: 'react_userBadge',
    create: createUserBadge,
    pjaxContainerDependencyIdList: [PJAX_MAIN_CONTAINER_ID, PJAX_EVENTS_CONTAINER_ID],
  },
  {
    targetId: 'react_signUp',
    create: createSignUp,
    pjaxContainerDependencyIdList: [],
  },
  {
    targetClass: 'react_customTooltip',
    create: createCustomTooltip,
    pjaxContainerDependencyIdList: [],
  },
];

const hasPjaxContainerDependency = (reactTree, containerId) => reactTree.pjaxContainerDependencyIdList.includes(containerId);

let reactTreeToUnmountList = [];

/**
 * For each listed React tree, instantiate a React root component from the DOM elements having the relevant id or class (if any).
 * Store unmount functions for further unloading of the instantiated React tree.
 * Note that React is not currently used for SPA purposes here.
 *
 * @param {String} pjaxContainerId - The id of the pjax container that triggered the mounting. Trees that do not depend on that container are not mounted.
 * @param {EnhancedStore} store - The redux store of the app. React trees will be able to access it.
 * @param {Boolean} forceLoad - Whether the React tree should be instantiated even if the targeted root already has children. Defaults to `false`.
 */
const mountReactTreesDependingOnPjaxContainer = (pjaxContainerId, store, forceLoad = false) => {
  reactTreeList.filter((reactTree) => forceLoad || hasPjaxContainerDependency(reactTree, pjaxContainerId)).forEach(({
    create,
    targetId,
    targetClass,
    shouldUnmountOnBeforeReplace,
    pjaxContainerDependencyIdList,
  }) => {
    const targetElementList = [];
    if (targetId) {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElementList.push(targetElement);
      }
    } else {
      targetElementList.push(...document.getElementsByClassName(targetClass));
    }
    targetElementList.forEach((targetElement) => {
      const unmount = create(targetElement, store);
      if (unmount) {
        const reactTreeToUnmount = {
          unmount,
          pjaxContainerDependencyIdList,
          shouldUnmountOnBeforeReplace,
        };
        reactTreeToUnmountList.push(reactTreeToUnmount);
      }
    });
  });
};

/**
 * Call each stored react tree unmount function if they depend on the given pjax container id.
 *
 * @param {String} pjaxContainerId - The id of the pjax container that triggered the unmounting. Trees that do not depend on that container are not unmounted.
 * @param {Boolean} isOnBeforeReplace
 */
const unmountReactTreesDependingOnPjaxContainer = (pjaxContainerId, isOnBeforeReplace = false) => {
  const shouldUnmount = (reactTreeToUnmount) => {
    const treeUnmountTimingMatchesEvent = (reactTreeToUnmount.shouldUnmountOnBeforeReplace ?? false) === isOnBeforeReplace;
    return hasPjaxContainerDependency(reactTreeToUnmount, pjaxContainerId) && treeUnmountTimingMatchesEvent;
  };
  // Unmount trees that depend on the given pjax container id
  reactTreeToUnmountList.forEach(reactTreeToUnmount => {
    if (shouldUnmount(reactTreeToUnmount)) {
      try {
        reactTreeToUnmount.unmount?.();
      } catch (error) {
        if (error instanceof DOMException) {
          console.error('Unmounting error', error);
          console.error('We caught the error, so this is probably ok for now. However, it indicates a potential issue with pjax navigation. Look for two pjax calls happening before the DOM replacement is done.');
          Sentry.captureException(error);
        } else {
          throw error;
        }
      }
    }
  });
  // Remove the unmounted trees from the list
  reactTreeToUnmountList = reactTreeToUnmountList.filter((reactTreeToUnmount) => !shouldUnmount(reactTreeToUnmount));
};

// Listener setup for react tree mounting and unmounting.
window.onload = () => {
  if (window.PARSYS_CLOUD_PREVENT_RENDERING) {
    return;
  }

  const store = initRedux();
  $(() => mountReactTreesDependingOnPjaxContainer(PJAX_MAIN_CONTAINER_ID, store, true));
  $(document).on('pjax:end', (event) => mountReactTreesDependingOnPjaxContainer(event.target.id, store));
  $(document).on('pjax:beforeReplace', (event) => unmountReactTreesDependingOnPjaxContainer(event.target.id, true));
  $(document).on('pjax:start', (event) => unmountReactTreesDependingOnPjaxContainer(event.target.id));
};

export let store;

function initRedux() {
  let middleware;
  if (!isProduction) {
    middleware = createMiddlewares(isProduction);
  } else {
    const sentryReduxEnhancer = Sentry.createReduxEnhancer({});
    middleware = compose(createMiddlewares(isProduction), sentryReduxEnhancer);
  }
  store = configureStore({
    reducer,
    middleware,
  });
  store.dispatch(appInit({
    browserSupport: checkBrowser(),
  }));
  return store;
}

export function pushToReactSnackbar(message) {
  if (store) {
    store.dispatch(pushSnackbar(message));
  }
}

window.pushToReactSnackbar = pushToReactSnackbar;
